/**
 * 独自センテンス（$_ で始まるもの）の読み書き。
 *
 * - `$_MARK`      … 原典 GoalView のゴール地点共有（`GoalView.cpp:1592`）
 * - `$_TVMARK`    … TV ツールの拡張。完全な UTM 座標＋半径（docs/03 §2）
 * - `$_TVCIRCLE`  … TV ツールの競技エリア（円）（docs/03 §3）
 *
 * ★ 設計方針（docs/03 §5）
 *   1. 未知のセンテンスは**破棄せず素通しする**。中継ストリームは TV など他の
 *      クライアントと共有された資源であり、捨てると相手の機能を壊す。
 *   2. 次世代 GV が独自に追加するものは `$_GV**` で名前空間を切る。
 */

import { withChecksum } from './nmea.ts';
import type { CircleArea, MarkPoint, UtmPoint } from './types.ts';
import { bandOf, fromEightDigit, fromUtm, toEightDigit, toUtm } from './utm.ts';

/** 次世代 GV が独自センテンスを足すときの接頭辞 */
export const GV_SENTENCE_PREFIX = '$_GV';

// ---------------------------------------------------------------------------
// $_MARK （GoalView 互換）
// ---------------------------------------------------------------------------

/**
 * `$_MARK,<名前1>,<名前2>,...`
 *
 * 中身は doc.kml の地点名か、8桁UTMコードのどちらか。座標は入っていないため、
 * 名前解決には地点マスタが、8桁コードの復元には 100km 区画の基準が要る。
 */
export function parseMark(sentence: string): string[] | null {
  const f = sentence.replace(/\*.*$/, '').split(',');
  if (f[0] !== '$_MARK') return null;
  return f.slice(1).filter((s) => s.length > 0);
}

export function buildMark(names: string[]): string {
  return `$_MARK${names.map((n) => `,${n}`).join('')}`;
}

/**
 * `$_MARK` の項目を地点に解決する。
 *
 * @param names   `parseMark` の結果
 * @param options `lookup` は地点名マスタ（doc.kml 由来）、`band` は8桁UTM復元の基準
 */
export function resolveMarkNames(
  names: string[],
  options: {
    lookup?: (name: string) => { lat: number; lon: number } | undefined;
    band?: { eastingBase: number; northingBase: number; zone: number };
  } = {},
): MarkPoint[] {
  const out: MarkPoint[] = [];
  for (const name of names) {
    const hit = options.lookup?.(name);
    if (hit) {
      out.push({ name, lat: hit.lat, lon: hit.lon, alt: null, radius: null });
      continue;
    }
    if (/^\d{8}$/.test(name) && options.band) {
      const utm = fromEightDigit(name, options.band);
      if (utm) {
        const ll = fromUtm(utm);
        out.push({ name, lat: ll.lat, lon: ll.lon, utm, alt: null, radius: null });
      }
    }
    // 解決できない項目は落とす。原典も同じ挙動（GoalView.cpp:1620）
  }
  return out;
}

/**
 * 地点リストから `$_MARK` 用の名前列を作る。
 *
 * `$_MARK` の項目は「受信側の doc.kml にある地点名」か「8桁UTMコード」でなければ
 * 解決されない（`GoalView.cpp:1606-1730`）。受信側がどの doc.kml を持っているかは
 * 分からないので、**座標があるときは既定で8桁UTMコードを使う**。
 * TV も `$_TVMARK` の名前が `2305` でありながら `$_MARK` には `23053241` を送っている。
 *
 * @param options.preferNames 受信側と地点マスタを共有していることが確実な場合に true。
 *                            名前をそのまま送る（原典 GoalView の振る舞い）。
 */
export function marksToNames(
  marks: MarkPoint[],
  options: { preferNames?: boolean } = {},
): string[] {
  return marks.map((m) => {
    const hasCoords = m.utm !== undefined || (Number.isFinite(m.lat) && Number.isFinite(m.lon));
    if (!hasCoords) return m.name;
    if (options.preferNames && m.name && !/^\d{8}$/.test(m.name)) return m.name;
    const utm = m.utm ?? toUtm(m.lon, m.lat);
    return toEightDigit(utm.easting, utm.northing);
  });
}

// ---------------------------------------------------------------------------
// $_TVMARK （TV 拡張）
// ---------------------------------------------------------------------------

/**
 * `$_TVMARK,<名前>/<東距>/<北距>/<半径>,...`
 *
 * ⚠️ 東距の桁数はストリーム途中で 5桁→6桁 に変わった実績がある（docs/03 §2）。
 *    フィールド幅を固定で仮定してはいけない。
 *
 * @param zone UTM ゾーン。**センテンスに含まれていない**ため外から与える必要がある
 *             （TV 側仕様の弱点。すり合わせ時の論点 / docs/03 §4）
 */
export function parseTvMark(sentence: string, zone: number): MarkPoint[] | null {
  const f = sentence.replace(/\*.*$/, '').split(',');
  if (f[0] !== '$_TVMARK') return null;
  const out: MarkPoint[] = [];
  for (const item of f.slice(1)) {
    if (!item) continue;
    const p = item.split('/');
    if (p.length < 3) continue;
    const easting = Number(p[1]);
    const northing = Number(p[2]);
    const radius = p.length > 3 ? Number(p[3]) : NaN;
    if (!Number.isFinite(easting) || !Number.isFinite(northing)) continue;
    const utm: UtmPoint = { easting, northing, zone };
    const ll = fromUtm(utm);
    out.push({
      name: p[0],
      lat: ll.lat,
      lon: ll.lon,
      utm,
      alt: null,
      radius: Number.isFinite(radius) ? radius : null,
    });
  }
  return out;
}

export function buildTvMark(marks: MarkPoint[]): string {
  const items = marks.map((m) => {
    const utm = m.utm ?? toUtm(m.lon, m.lat);
    const r = m.radius ?? 0;
    return `${m.name}/${Math.round(utm.easting)}/${Math.round(utm.northing)}/${Math.round(r)}`;
  });
  return `$_TVMARK${items.map((i) => `,${i}`).join('')}`;
}

// ---------------------------------------------------------------------------
// $_TVCIRCLE （TV 拡張）
// ---------------------------------------------------------------------------

/**
 * `$_TVCIRCLE,<課題>/<要素ID>/<東距>/<北距>/<半径>/<色>/<種別>,...`
 *
 * 実例: `3DT/3DT_CENTER/427008/5123836/4000/#ff00aa/S`
 *       `3DT/3DT_RMIN/427008/5123836/2000/#ff00aa/S`
 * 同一中心・異なる半径の対で、ドーナツ状のスコアリングエリアを表す。
 */
export function parseTvCircle(sentence: string, zone: number): CircleArea[] | null {
  const f = sentence.replace(/\*.*$/, '').split(',');
  if (f[0] !== '$_TVCIRCLE') return null;
  const out: CircleArea[] = [];
  for (const item of f.slice(1)) {
    if (!item) continue;
    const p = item.split('/');
    if (p.length < 5) continue;
    const easting = Number(p[2]);
    const northing = Number(p[3]);
    const radius = Number(p[4]);
    if (!Number.isFinite(easting) || !Number.isFinite(northing) || !Number.isFinite(radius)) {
      continue;
    }
    const utm: UtmPoint = { easting, northing, zone };
    const ll = fromUtm(utm);
    out.push({
      group: p[0],
      id: p[1],
      lat: ll.lat,
      lon: ll.lon,
      utm,
      radius,
      color: p[5] || undefined,
      // 種別の意味は未確定なので生値のまま保持する
      style: p[6] || undefined,
    });
  }
  return out;
}

export function buildTvCircle(circles: CircleArea[]): string {
  const items = circles.map((c) => {
    const utm = c.utm ?? toUtm(c.lon, c.lat);
    const parts = [
      c.group,
      c.id,
      String(Math.round(utm.easting)),
      String(Math.round(utm.northing)),
      String(Math.round(c.radius)),
    ];
    if (c.color) parts.push(c.color);
    if (c.style) parts.push(c.style);
    return parts.join('/');
  });
  return `$_TVCIRCLE${items.map((i) => `,${i}`).join('')}`;
}

// ---------------------------------------------------------------------------
// 併送ヘルパー
// ---------------------------------------------------------------------------

/**
 * 地点共有を「互換形式＋拡張形式」の組で作る。
 *
 * TV が `$_MARK` と `$_TVMARK` を同時に流しているのと同じ流儀（docs/03 §1）。
 * 既存 GoalView は `$_MARK` だけを読み、対応クライアントは拡張側を読む。
 */
export function buildMarkPair(marks: MarkPoint[]): string[] {
  return [buildTvMark(marks), buildMark(marksToNames(marks))];
}

/** 地点群から 100km 区画の基準を推定する（8桁UTM の復元に使う） */
export function bandFromMarks(marks: MarkPoint[]): ReturnType<typeof bandOf> | null {
  const first = marks.find((m) => m.utm);
  return first?.utm ? bandOf(first.utm) : null;
}

/** 独自センテンスにチェックサムを付ける（原典の $_MARK は付けていないので任意） */
export function signSentence(sentence: string): string {
  return withChecksum(sentence);
}
